/**
 * The single way the app talks to its backend.
 *
 * Every response is reduced to `{ data } | { error }` so no caller has to think
 * about status codes, and `error.code` carries the server's machine-readable
 * reason. Callers branch on that code, never on message text — matching on copy
 * is how error handling silently breaks the first time wording changes.
 *
 * A failure is always surfaced as an error. There is no path here that turns an
 * unreachable backend into an empty success.
 */

export interface ApiError {
  code: string;
  message: string;
  status: number;
}

export type ApiResult<T> = { data: T; error: null } | { data: null; error: ApiError };

const DEFAULT_TIMEOUT_MS = 15_000;
const UPLOAD_TIMEOUT_MS = 120_000;

/**
 * Same-origin in the browser: Vite proxies `/api` in development and the server
 * serves the built client in production. The native build overrides this with a
 * fully-qualified origin at build time.
 */
const API_BASE = (
  import.meta.env.VITE_API_URL ??
  import.meta.env.VITE_API_BASE_URL ??
  ''
).replace(/\/$/, '');

/** Absolute API host (Pages → Workers, native) cannot rely on same-site cookies. */
const CROSS_ORIGIN_API = /^https?:\/\//i.test(API_BASE);
const BEARER_SESSION_KEY = 'elix_bearer';

/**
 * Bearer token. In-memory always. For cross-origin API hosts, also mirrored to
 * sessionStorage so a reload can restore `/api/auth/me` (third-party cookies are
 * blocked in modern browsers even with SameSite=None). Same-origin Coolify keeps
 * HttpOnly cookie authority and does not write the token to web storage.
 */
let bearerToken: string | null = null;

function readStoredBearer(): string | null {
  if (!CROSS_ORIGIN_API || typeof sessionStorage === 'undefined') return null;
  try {
    const value = sessionStorage.getItem(BEARER_SESSION_KEY);
    return value && value.length > 0 ? value : null;
  } catch {
    return null;
  }
}

export function setBearerToken(token: string | null): void {
  bearerToken = token;
  if (!CROSS_ORIGIN_API || typeof sessionStorage === 'undefined') return;
  try {
    if (token) sessionStorage.setItem(BEARER_SESSION_KEY, token);
    else sessionStorage.removeItem(BEARER_SESSION_KEY);
  } catch {
    /* private mode / blocked storage — memory token still works for this page */
  }
}

if (bearerToken === null) {
  bearerToken = readStoredBearer();
}

function fail(status: number, code: string, message: string): { data: null; error: ApiError } {
  return { data: null, error: { code, message, status } };
}

export async function request<T>(path: string, init: RequestInit = {}): Promise<ApiResult<T>> {
  const controller = new AbortController();
  const headers = new Headers(init.headers);
  const reqBody = init.body;
  const skipJsonContentType =
    (typeof FormData !== 'undefined' && reqBody instanceof FormData) ||
    (typeof Blob !== 'undefined' && reqBody instanceof Blob);
  if (reqBody !== undefined && !headers.has('Content-Type') && !skipJsonContentType) {
    headers.set('Content-Type', 'application/json');
  }
  if (bearerToken !== null) headers.set('Authorization', `Bearer ${bearerToken}`);

  const timeoutMs =
    skipJsonContentType || /\/api\/uploads(\/|$)/.test(path) ? UPLOAD_TIMEOUT_MS : DEFAULT_TIMEOUT_MS;
  const timeout = setTimeout(() => controller.abort(), timeoutMs);

  let response: Response;
  try {
    response = await fetch(`${API_BASE}${path}`, {
      credentials: 'include',
      ...init,
      headers,
      signal: init.signal ?? controller.signal,
    });
  } catch (err) {
    if (err instanceof DOMException && err.name === 'AbortError') {
      return fail(0, 'aborted', 'The request was cancelled.');
    }
    return fail(0, 'network_error', 'Cannot reach the server. Check your connection and try again.');
  } finally {
    clearTimeout(timeout);
  }

  if (response.status === 204) {
    return { data: undefined as T, error: null };
  }

  let parsed: unknown = null;
  const text = await response.text();
  if (text.length > 0) {
    try {
      parsed = JSON.parse(text);
    } catch {
      return fail(response.status, 'invalid_response', 'The server returned an unexpected response.');
    }
  }

  if (!response.ok) {
    const flat = parsed as {
      error?: { code?: unknown; message?: unknown };
      code?: unknown;
      message?: unknown;
    } | null;
    const envelope = flat?.error;
    const code =
      (typeof envelope?.code === 'string' && envelope.code) ||
      (typeof flat?.code === 'string' && flat.code) ||
      'server_error';
    const message =
      (typeof envelope?.message === 'string' && envelope.message) ||
      (typeof flat?.message === 'string' && flat.message) ||
      'Something went wrong. Please try again.';
    return fail(response.status, code, message);
  }

  return { data: parsed as T, error: null };
}
